/** QuickRouter — Gabarits de cartes : contour, trous de fixation et connecteurs imposés
 *  par la carte mère (posés verrouillés, avec les NOMS des signaux de la carte mère).
 *
 *  Toutes les cotes partent du coin bas-gauche de la carte, vue de dessus.
 */
import { pinHeader } from './footprints.js';
import { VQ_BOTTOM_FOOTPRINTS } from './ventuno-bottom.js';
import { roundedRect } from '../schema.js';

// ── Connecteurs propres aux gabarits ───────────────────────────

const UNO_DESC = 'Connecteur de la carte mère au format Arduino UNO R3 : ses broches portent les noms des signaux de la carte (5V, GND, D2, A0, SDA…). Brochez vos nets sur ces noms.';

export const TEMPLATE_FOOTPRINTS = [
  pinHeader('uno-power', 'UNO R3 — alimentation (1×8)', 8, 1, { names: ['NC', 'IOREF', 'RESET', '3V3', '5V', 'GND', 'GND', 'VIN'], category: 'Cartes mères', description: UNO_DESC }),
  pinHeader('uno-analog', 'UNO R3 — analogiques A0-A5 (1×6)', 6, 1, { names: ['A0', 'A1', 'A2', 'A3', 'A4', 'A5'], category: 'Cartes mères', description: UNO_DESC }),
  pinHeader('uno-digital-low', 'UNO R3 — numériques D0-D7 (1×8)', 8, 1, { names: ['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'], category: 'Cartes mères', description: UNO_DESC }),
  pinHeader('uno-digital-high', 'UNO R3 — numériques D8-SCL (1×10)', 10, 1, { names: ['D8', 'D9', 'D10', 'D11', 'D12', 'D13', 'GND', 'AREF', 'SDA', 'SCL'], category: 'Cartes mères', description: UNO_DESC }),
  pinHeader('rpi-gpio-40', 'Raspberry Pi — GPIO 40 broches (2×20)', 20, 2, {
    rowDir: 1,
    female: true,
    names: [
      '3V3', '5V', 'GPIO2', '5V', 'GPIO3', 'GND', 'GPIO4', 'GPIO14', 'GND', 'GPIO15',
      'GPIO17', 'GPIO18', 'GPIO27', 'GND', 'GPIO22', 'GPIO23', '3V3', 'GPIO24', 'GPIO10', 'GND',
      'GPIO9', 'GPIO25', 'GPIO11', 'GPIO8', 'GND', 'GPIO7', 'ID_SD', 'ID_SC', 'GPIO5', 'GND',
      'GPIO6', 'GPIO12', 'GPIO13', 'GND', 'GPIO19', 'GPIO16', 'GPIO26', 'GPIO20', 'GND', 'GPIO21',
    ],
    category: 'Cartes mères',
    description: 'Connecteur GPIO 40 broches du Raspberry Pi (vue de dessus du HAT). Broche 1 = 3V3 (rangée intérieure), broches paires sur la rangée du bord. I2C : GPIO2 = SDA, GPIO3 = SCL ; UART : GPIO14 = TXD, GPIO15 = RXD ; SPI0 : GPIO10 MOSI, GPIO9 MISO, GPIO11 SCLK, GPIO8 CE0.',
  }),
];

// ── Arduino VENTUNO Q (ABX00181) ───────────────────────────────
//
// Repère = plan mécanique de la fiche (§12, vue de dessus, carte en PORTRAIT 100 × 160 mm),
// origine en bas à gauche. Le contour, les trous et les connecteurs ont été relevés sur le
// dessin vectoriel de la fiche (échelle calée sur les 160 mm) : la zone UNO tombe alors
// EXACTEMENT aux cotes Arduino, et le JHAT aux cotes d'un HAT Raspberry Pi décalé de 6 mm.

const VQ_UNO = 'Connecteur UNO de la VENTUNO Q, piloté par le MCU STM32H5F5 en logique 3,3 V. Les connecteurs 3,3 V et 5 V des shields sont protégés par des interrupteurs de charge limités à 2,8 A.';

TEMPLATE_FOOTPRINTS.push(
  pinHeader('vq-janalog-power', 'VENTUNO Q — JANALOG 1-8 (alimentation)', 8, 1, {
    names: ['NC', 'IOREF', 'RESET', '3V3', '5V', 'GND', 'GND', 'VIN'],
    category: 'Cartes mères',
    description: `${VQ_UNO} JANALOG broches 1 à 8 : 1 NC (strap BOOT0 du MCU, ne rien y relier), 2 IOREF (sortie de référence 3,3 V, ne pas alimenter par là), 3 RESET (NRST du MCU), 4 +3V3 OUT, 5 +5V (limité USB), 6-7 GND, 8 VIN 7-24 V (fusible réarmable 1,1 A : sert à alimenter un shield, pas à alimenter la carte à pleine charge).`,
  }),
  pinHeader('vq-janalog-analog', 'VENTUNO Q — JANALOG 9-14 (A0-A5)', 6, 1, {
    names: ['A0', 'A1', 'A2', 'A3', 'A4', 'A5'],
    category: 'Cartes mères',
    description: `${VQ_UNO} JANALOG broches 9 à 14 = A0 (PA4), A1 (PA5), A2 (PE12, SPI4_SCK), A3 (PE13, SPI4_MISO), A4 (PE14, SPI4_MOSI), A5 (PE15). Entrées CAN 0-3,3 V, NON tolérantes au 5 V. A4/A5 n'ont PAS d'I2C matériel : utiliser SDA/SCL de JDIGITAL.`,
  }),
  pinHeader('vq-jdigital-low', 'VENTUNO Q — JDIGITAL 1-8 (D0-D7)', 8, 1, {
    names: ['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'],
    category: 'Cartes mères',
    description: `${VQ_UNO} JDIGITAL broches 1 à 8 : D0 = RX (PB11, UART), D1 = TX (PB10), D2 (PB0), D3 (PB1, PWM), D4 (PB6, FDCAN2_TX), D5 (PB5, PWM, FDCAN2_RX), D6 (PB2, PWM), D7 (PB3). La plupart tolèrent 5 V en entrée numérique.`,
  }),
  pinHeader('vq-jdigital-high', 'VENTUNO Q — JDIGITAL 9-18 (D8-SCL)', 10, 1, {
    names: ['D8', 'D9', 'D10', 'D11', 'D12', 'D13', 'GND', 'AREF', 'SDA', 'SCL'],
    category: 'Cartes mères',
    description: `${VQ_UNO} JDIGITAL broches 9 à 18 : D8 (PB4), D9 (PB7, PWM), D10 = CS (PB12), D11 = MOSI (PB15), D12 = MISO (PB14), D13 = SCK (PB13), GND, AREF (actif seulement si PI8 est haut), SDA (PH12) et SCL (PH11) = I2C4 matériel.`,
  }),
  pinHeader('vq-jhat', 'VENTUNO Q — JHAT 40 broches (2×20)', 20, 2, {
    rowDir: 1,
    female: true,
    names: [
      '3V3', '5V', 'GPIO2', '5V', 'GPIO3', 'GND', 'GPIO4', 'GPIO14', 'GND', 'GPIO15',
      'GPIO17', 'GPIO18', 'GPIO27', 'GND', 'GPIO22', 'GPIO23', '3V3', 'GPIO24', 'GPIO10', 'GND',
      'GPIO9', 'GPIO25', 'GPIO11', 'GPIO8', 'GND', 'GPIO7', 'GPIO0', 'GPIO1', 'GPIO5', 'GND',
      'GPIO6', 'GPIO12', 'GPIO13', 'GND', 'GPIO19', 'GPIO16', 'GPIO26', 'GPIO20', 'GND', 'GPIO21',
    ],
    category: 'Cartes mères',
    description: 'JHAT de la VENTUNO Q, brochage Raspberry Pi, piloté par le MPU QCS8275 et ramené en 3,3 V par des convertisseurs TXS0108/TXS0104 : jamais plus de 3,3 V sur un signal ; 3V3 et 5V sont des SORTIES (ne pas réalimenter). I2C1 : GPIO2 SDA / GPIO3 SCL ; SPI0 : GPIO10 MOSI, GPIO9 MISO, GPIO11 SCLK, GPIO8 CE0, GPIO7 CE1 ; I2S : GPIO18 CLK, GPIO19 FS, GPIO20 DIN, GPIO21 DOUT ; GPIO0/GPIO1 = I2C0 de l\'EEPROM d\'identification. ⚠ GPIO14/15 (UART), GPIO17, GPIO16, GPIO26, GPIO20, GPIO21 sont partagés avec le module Wi-Fi/Bluetooth : indisponibles quand le Bluetooth est actif.',
  }),
);

TEMPLATE_FOOTPRINTS.push(...VQ_BOTTOM_FOOTPRINTS);

/** Contour de la VENTUNO Q (pans coupés relevés sur le plan). */
const VQ_OUTLINE = [[0, 0], [96, 0], [96, 4], [100, 8], [100, 120], [93.96, 126], [93.96, 157.75], [91.71, 160], [0, 160]];

/** Ce qui dépasse du dessus de la VENTUNO Q (hauteurs au-dessus de son circuit). */
const VQ_OBSTACLES = {
  heatsink: { label: 'Dissipateur + ventilateur du SoM', kind: 'rect', min: [1.4, 66.7], max: [84.4, 149.7], height: 24.2, note: 'hauteur totale de la carte 25,8 mm (fiche) ; emprise approximative autour des 4 entretoises M2.5' },
  facade: { label: 'Connecteurs de façade (Ethernet, USB-A, HDMI, USB-C, bornier, jack)', kind: 'rect', min: [77, 8], max: [102, 122], height: 14, note: 'emprise approximative ; RJ45 ≈ 13,5 mm' },
  cameras: { label: 'Connecteurs caméra MIPI (×3)', kind: 'rect', min: [10, 150], max: [82, 160], height: 2.5, note: 'emprise approximative' },
  jctl: { label: 'JCTL (2×5, débogage MPU)', kind: 'rect', min: [14.7, 14.57], max: [19.8, 27.27], height: 8.5, note: 'barrette mâle : hauteur typique, à mesurer' },
  m2: { label: 'Carte M.2 2230 + connecteur', kind: 'rect', min: [23.2, 23.2], max: [59.2, 45.2], height: 5, note: 'entretoise M2 de 4 mm en (57,23 ; 34,22)' },
  button: { label: 'Bouton utilisateur', kind: 'rect', min: [3.25, 46.3], max: [9.45, 52.5], height: 5, note: 'hauteur à mesurer' },
  nut: { label: 'Écrou M3 du trou de fixation', kind: 'circle', c: [14.12, 6.6], r: 3.5, height: 2.5, note: 'si la carte est montée sur entretoises' },
};

const vqHeader = (min, max) => ({ kind: 'rect', min, max, height: 8.5 });

/** Ce qui dépasse du DESSOUS de la VENTUNO Q (vers une carte porteuse). */
const VQ_UNDER = [
  { label: 'Pattes des connecteurs UNO (JANALOG)', kind: 'rect', min: [26.67, 1.27], max: [64.77, 3.81], height: 3, note: 'traversants : longueur des pattes à mesurer' },
  { label: 'Pattes des connecteurs UNO (JDIGITAL)', kind: 'rect', min: [17.53, 49.53], max: [64.77, 52.07], height: 3, note: 'traversants : longueur des pattes à mesurer' },
  { label: 'Pattes du JHAT', kind: 'rect', min: [7.09, 56.46], max: [57.89, 61.54], height: 3, note: 'traversants : longueur des pattes à mesurer' },
  { label: 'Pattes des connecteurs de façade et du bornier', kind: 'rect', min: [77, 8], max: [102, 122], height: 3, note: 'emprise approximative' },
];

/** Position des connecteurs du dessous (centre, repère du gabarit), relevée sur la vue de dessous du plan. */
const VQ_BOTTOM = {
  jmisc: { ref: 'J6', footprint: 'vq-jmisc-carrier', pos: [46.35, 101.45], rot: 0, locked: true, value: 'JMISC 2x30' },
  jmedia: { ref: 'J7', footprint: 'vq-jmedia-carrier', pos: [46.35, 148.315], rot: 0, locked: true, value: 'JMEDIA 2x30' },
  jomega: { ref: 'J8', footprint: 'vq-jomega-carrier', pos: [41.682, 44.16], rot: 0, locked: true, value: 'JOMEGA 2x50' },
};

function ventunoQ(forme, empilage = 5) {
  const uno = [
    { ref: 'J1', footprint: 'vq-janalog-power', pos: [27.94, 2.54], rot: 0, locked: true, value: 'JANALOG 1-8' },
    { ref: 'J2', footprint: 'vq-janalog-analog', pos: [50.8, 2.54], rot: 0, locked: true, value: 'JANALOG 9-14' },
    { ref: 'J3', footprint: 'vq-jdigital-low', pos: [63.5, 50.8], rot: 180, locked: true, value: 'JDIGITAL 1-8' },
    { ref: 'J4', footprint: 'vq-jdigital-high', pos: [41.656, 50.8], rot: 180, locked: true, value: 'JDIGITAL 9-18' },
  ];
  // JHAT : centré entre les deux trous HAT (entraxe 58 mm, ligne y = 59), broche 1 à gauche
  // sur la rangée intérieure (vers la zone UNO), comme sur un Raspberry Pi.
  const jhat = { ref: 'J5', footprint: 'vq-jhat', pos: [8.36, 57.73], rot: 0, locked: true, value: 'JHAT 2x20' };
  const O = VQ_OBSTACLES;
  const mother = (gap, obstacles, side = 'below') => ({ name: 'Arduino VENTUNO Q', outline: VQ_OUTLINE, thickness: 1.6, side, gap, obstacles });

  if (forme === 3) {
    // Porteuse au format UNO sous la VENTUNO Q, sur JMISC + JMEDIA (emplacement « UNO CARRIER »
    // sérigraphié au dos, celui de la UNO Media Carrier) : 53,34 × 68,58, calée sur le bord caméras.
    return {
      outline: roundedRect(53.34, 68.58, 1).map(([x, y]) => [Math.round((x + 19.68) * 1e4) / 1e4, Math.round((y + 91.42) * 1e4) / 1e4]),
      components: [VQ_BOTTOM.jmisc, VQ_BOTTOM.jmedia],
      mother: mother(empilage, [], 'above'),
    };
  }
  if (forme === 4) {
    // Grande porteuse au contour de la VENTUNO Q, sur les trois connecteurs du dessous, fixée
    // par entretoises aux 4 trous des coins.
    return {
      outline: VQ_OUTLINE,
      components: [
        VQ_BOTTOM.jmisc, VQ_BOTTOM.jmedia, VQ_BOTTOM.jomega,
        hole('H1', [3.5, 156], 'trou-m3'), hole('H2', [89.96, 156], 'trou-m3-npth'), // contre le pan coupé
        hole('H3', [14.12, 6.6], 'trou-m3'), hole('H4', [91.96, 4], 'trou-m3'),
      ],
      mother: mother(empilage, VQ_UNDER, 'above'),
    };
  }

  if (forme === 1) {
    // HAT 65 × 56,5 posé sur le JHAT : repère HAT décalé de +6 mm en Y. Seuls les deux trous
    // du haut existent sur la VENTUNO Q ; la carte fille passe au-dessus des connecteurs UNO.
    return {
      outline: roundedRect(65, 56.5, 3).map(([x, y]) => [x, y + 6]),
      components: [jhat, hole('H1', [3.5, 59], 'trou-m3-npth'), hole('H2', [61.48, 59], 'trou-m3-npth')],
      mother: mother(11, [
        O.nut, O.jctl, O.m2, O.button,
        { label: 'Connecteurs UNO femelles (JANALOG)', ...vqHeader([26.67, 1.27], [64.77, 3.81]) },
        { label: 'Connecteurs UNO femelles (JDIGITAL)', ...vqHeader([17.53, 49.53], [64.77, 52.07]) },
      ]),
    };
  }
  if (forme === 2) {
    // Grande carte fille enfichée à la fois sur l'UNO et sur le JHAT (même hauteur d'empilage :
    // 8,5 mm de femelle + 2,5 mm de corps de barrette mâle), arrêtée sous le dissipateur
    // (y < 66,7) et avant les connecteurs de façade (x < 77).
    return {
      outline: roundedRect(76, 64.5, 1),
      components: [
        ...uno, jhat,
        hole('H1', [14.12, 6.6], 'trou-m3-npth'), hole('H2', [3.5, 59], 'trou-m3-npth'), hole('H3', [61.48, 59], 'trou-m3-npth'),
      ],
      mother: mother(11, [O.nut, O.jctl, O.m2, O.button, O.heatsink, O.facade]),
    };
  }
  // Shield UNO : contour UNO standard, dans le repère de la VENTUNO Q.
  return {
    outline: UNO_OUTLINE,
    components: uno,
    mother: mother(11, [O.nut, O.jctl, O.m2, O.button]),
  };
}

// ── Gabarits ───────────────────────────────────────────────────

/** Contour du shield Arduino UNO (pan coupé à droite, comme la carte d'origine). */
const UNO_OUTLINE = [
  [0, 0], [66.04, 0], [68.58, 2.54], [68.58, 38.1], [66.04, 40.64], [66.04, 51.82], [64.52, 53.34], [0, 53.34],
];

const hole = (ref, pos, footprint = 'trou-m3') => ({ ref, footprint, pos, rot: 0, locked: true });

export const BOARD_TEMPLATES = [
  {
    id: 'uno-shield',
    name: 'Shield format UNO R3 (Arduino UNO, UNO Q)',
    description: [
      'Carte fille 68,58 × 53,34 mm aux connecteurs Arduino UNO R3 : alimentation (J1), analogiques (J2), D0-D7 (J3), D8-SCL (J4) — les broches portent les noms des signaux.',
      'Logique 5 V sur l\'UNO R3 ; 3,3 V sur l\'UNO Q (STM32 côté connecteurs) : vérifier la tension des E/S de la carte cible avant de câbler.',
      'Pour la VENTUNO Q, prendre son gabarit dédié (ventuno-q) : il connaît la carte et ses obstacles.',
    ].join(' '),
    params: [],
    build() {
      return {
        outline: UNO_OUTLINE,
        components: [
          { ref: 'J1', footprint: 'uno-power', pos: [27.94, 2.54], rot: 0, locked: true, value: 'UNO POWER' },
          { ref: 'J2', footprint: 'uno-analog', pos: [50.8, 2.54], rot: 0, locked: true, value: 'UNO ANALOG' },
          { ref: 'J3', footprint: 'uno-digital-low', pos: [63.5, 50.8], rot: 180, locked: true, value: 'UNO D0-D7' },
          { ref: 'J4', footprint: 'uno-digital-high', pos: [41.656, 50.8], rot: 180, locked: true, value: 'UNO D8-SCL' },
          hole('H1', [13.97, 2.54], 'trou-m3-npth'), hole('H2', [15.24, 50.8], 'trou-m3-npth'), hole('H3', [66.04, 7.62], 'trou-m3-npth'), hole('H4', [66.04, 35.56], 'trou-m3-npth'),
        ],
      };
    },
  },
  {
    id: 'rpi-hat',
    name: 'HAT Raspberry Pi (65 × 56,5 mm)',
    description: 'Carte fille au format HAT : connecteur GPIO 40 broches (J1, noms des GPIO), 4 trous M2.5 aux entraxes du Pi (58 × 49,5 mm, rangée du haut alignée sur le connecteur). E/S en 3,3 V uniquement.',
    params: [],
    build() {
      return {
        outline: roundedRect(65, 56.5, 3),
        components: [
          { ref: 'J1', footprint: 'rpi-gpio-40', pos: [8.37, 51.73], rot: 0, locked: true, value: 'GPIO 2x20' },
          hole('H1', [3.5, 3.5], 'trou-m2_5'), hole('H2', [61.5, 3.5], 'trou-m2_5'), hole('H3', [3.5, 53], 'trou-m2_5'), hole('H4', [61.5, 53], 'trou-m2_5'),
        ],
      };
    },
  },
  {
    id: 'ventuno-q',
    name: 'Arduino VENTUNO Q (carte fille dédiée)',
    description: [
      'Carte fille pour l\'Arduino VENTUNO Q (ABX00181, 160 × 100 mm), dans le repère de son plan mécanique officiel : vue de dessus, carte en portrait, caméras en haut, Ethernet/USB/HDMI/bornier sur le bord droit, zone UNO en bas à gauche.',
      'Forme 0 = shield sur les connecteurs UNO (JANALOG = J1 + J2, JDIGITAL = J3 + J4) ; 1 = HAT sur le JHAT 40 broches (J5) ; 2 = grande carte 76 × 64,5 mm sur l’UNO ET le JHAT, arrêtée sous le dissipateur (11 mm entre les cartes avec des barrettes standard). Cartes PORTEUSES sous la VENTUNO Q, sur ses connecteurs du dessous au pas de 1,27 mm : 3 = format UNO sur JMISC (J6) + JMEDIA (J7), à l’emplacement « UNO CARRIER » ; 4 = contour complet sur JMISC + JMEDIA + JOMEGA (J8), 4 trous M3 aux coins. Pour une porteuse, la VENTUNO Q est AU-DESSUS : hauteur d’empilage selon la barrette mâle choisie (paramètre empilage).',
      'La VENTUNO Q est dessinée en fantôme sous la carte fille avec ses obstacles (dissipateur 25,8 mm, carte M.2, JCTL, bouton, écrou de fixation, connecteurs de façade) : le DRC signale toute collision.',
      'Toutes les E/S sont en 3,3 V : UNO (MCU STM32H5, la plupart des broches numériques tolèrent 5 V en entrée, A0-A5 NON), JHAT (MPU via convertisseurs de niveau, jamais plus de 3,3 V).',
    ].join(' '),
    params: [
      { id: 'forme', label: 'Forme (0 shield UNO, 1 HAT, 2 UNO + JHAT, 3 porteuse UNO dessous, 4 grande porteuse dessous)', default: 0, min: 0, max: 4 },
      { id: 'empilage', label: 'Porteuse : hauteur entre les cartes (mm)', default: 5, min: 1, max: 30 },
    ],
    build({ forme = 0, empilage = 5 } = {}) {
      return ventunoQ(Math.round(forme), empilage);
    },
  },
  {
    id: 'libre',
    name: 'Carte libre (rectangle)',
    description: 'Rectangle aux dimensions voulues, coins arrondis, 4 trous M3 optionnels à 4 mm des bords. Pour une carte autonome : nœud d\'E/S ESP32, interface RS485, alimentation…',
    params: [
      { id: 'w', label: 'Largeur (mm)', default: 80, min: 5, max: 500 },
      { id: 'h', label: 'Hauteur (mm)', default: 60, min: 5, max: 500 },
      { id: 'r', label: 'Rayon des coins (mm)', default: 2, min: 0, max: 20 },
      { id: 'holes', label: 'Trous M3 aux coins (0/1)', default: 1, min: 0, max: 1 },
    ],
    build({ w = 80, h = 60, r = 2, holes = 1 } = {}) {
      const comps = [];
      if (holes && w > 16 && h > 16) {
        comps.push(hole('H1', [4, 4]), hole('H2', [w - 4, 4]), hole('H3', [4, h - 4]), hole('H4', [w - 4, h - 4]));
      }
      return { outline: roundedRect(w, h, Math.min(r, w / 2, h / 2)), components: comps };
    },
  },
];

export const templateById = (id) => BOARD_TEMPLATES.find((t) => t.id === id) ?? null;
